import { FC, useMemo, useState } from "react"
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Divider,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import {
  ExpandMore,
  ArrowUpward,
  ArrowDownward,
  DeleteOutline,
  Add,
} from "@mui/icons-material"
import { v4 as uuidv4 } from "uuid"
import { NodeFormState, ActionNodeForm } from "./chainSerializer"
import { Slot } from "./slot"
import { ParamsForm } from "./paramsForm"
import { ConditionExprEditor } from "../fields/conditionExprEditor"
import {
  ON_ERROR_OPTIONS,
  SAVE_ACTIONS,
  TEMPLATE_HINTS,
} from "../../constants/hints"
import { Userbot, UserbotCatalog } from "../../interfaces"
import { isBlank } from "../common/jsonUtils"
import { useLazyClientsQuery } from "../../store"
import { useInfiniteSelectList } from "@/core/hooks"
import { infiniteListboxProps } from "@/core/components"
import { clientIdOptions, clientValueLabel } from "./callChainFields"

interface IProps {
  node: NodeFormState
  index: number
  total: number
  descriptors: UserbotCatalog.ActionDescriptor[]
  nodeKinds: UserbotCatalog.NodeDescriptor[]
  limits?: UserbotCatalog.CatalogLimits
  advanced: boolean
  issues?: UserbotCatalog.ValidationIssue[]
  extraHints?: string[]
  /** Сборщик {{stepId.result.*}}-плейсхолдеров для автокомплита полей узла. */
  getResultHints?: (currentNodeId: string) => string[]
  pathPrefix?: string
  onChange: (node: NodeFormState) => void
  onRemove: () => void
  onMove: (dir: -1 | 1) => void
}

const KIND_COLORS: Record<
  string,
  "primary" | "success" | "warning" | "info" | "secondary"
> = {
  action: "primary",
  branch: "warning",
  forEach: "info",
  callChain: "secondary",
  wait: "success",
  "flow.onUpdate": "info",
}

export const NodeEditor: FC<IProps> = ({
  node,
  index,
  total,
  descriptors,
  nodeKinds,
  limits,
  advanced,
  issues,
  extraHints = [],
  getResultHints,
  pathPrefix = "",
  onChange,
  onRemove,
  onMove,
}) => {
  const [kindMenuAnchor, setKindMenuAnchor] = useState<null | HTMLElement>(null)
  const allHints = useMemo(() => {
    const resultHints = getResultHints ? getResultHints(node.id) : []
    return [...extraHints, ...TEMPLATE_HINTS, ...resultHints]
  }, [extraHints, getResultHints, node.id])

  const [lazyClients] = useLazyClientsQuery()
  const clientsList = useInfiniteSelectList<Userbot.Client>({
    fetchPage: (p) => lazyClients(p).unwrap(),
    getId: (c) => c.id,
  })
  const clientOptions = useMemo(
    () => clientIdOptions(clientsList.items, allHints),
    [clientsList.items, allHints],
  )

  const patch = (over: Partial<NodeFormState>) =>
    onChange({ ...node, ...over } as NodeFormState)

  const nodeKey = pathPrefix + node.id
  const actionOptions = descriptors.map((d) => ({
    id: d.name,
    label: d.title,
    group: d.category,
  }))

  const renderCommonFields = () => (
    <AccordionDetails>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          <TextField
            label="ID узла"
            value={node.id}
            onChange={(e) => patch({ id: e.target.value } as never)}
            size="small"
            sx={{ flex: 1 }}
            helperText={`ctx-ключ: ${nodeKey}`}
            error={issues?.some((i) => i.path?.includes(".id"))}
          />
          <TextField
            select
            label="onError"
            value={node.onError ?? "fail"}
            onChange={(e) =>
              patch({ onError: e.target.value as Userbot.StepOnError } as never)
            }
            size="small"
            sx={{ minWidth: 130 }}
          >
            {ON_ERROR_OPTIONS.map((o) => (
              <MenuItem key={o} value={o}>
                {o}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        {advanced && (
          <Stack direction="row" spacing={2}>
            <TextField
              label="retry attempts"
              value={node.retryAttempts ?? ""}
              onChange={(e) =>
                patch({ retryAttempts: e.target.value } as never)
              }
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="retry backoff (ms)"
              value={node.retryBackoffMs ?? ""}
              onChange={(e) =>
                patch({ retryBackoffMs: e.target.value } as never)
              }
              size="small"
              sx={{ flex: 1 }}
            />
            <TextField
              label="timeout (ms)"
              value={node.timeoutMs ?? ""}
              onChange={(e) => patch({ timeoutMs: e.target.value } as never)}
              size="small"
              sx={{ flex: 1 }}
            />
          </Stack>
        )}
        {advanced && (
          <Box>
            <Typography variant="caption" color="text.secondary">
              save[]
            </Typography>
            <Stack spacing={1} mt={0.5}>
              {node.saves.map((s, i) => (
                <Stack key={i} direction="row" spacing={1}>
                  <TextField
                    select
                    label="action"
                    value={s.action}
                    onChange={(e) => {
                      const saves = [...node.saves]
                      saves[i] = { ...s, action: e.target.value as never }
                      patch({ saves } as never)
                    }}
                    size="small"
                    sx={{ minWidth: 130 }}
                  >
                    {SAVE_ACTIONS.map((a) => (
                      <MenuItem key={a} value={a}>
                        {a}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="params (JSON)"
                    value={s.paramsJson}
                    onChange={(e) => {
                      const saves = [...node.saves]
                      saves[i] = { ...s, paramsJson: e.target.value }
                      patch({ saves } as never)
                    }}
                    size="small"
                    multiline
                    minRows={1}
                    fullWidth
                    InputProps={{ style: { fontFamily: "monospace" } }}
                  />
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() =>
                      patch({
                        saves: node.saves.filter((_, j) => j !== i),
                      } as never)
                    }
                  >
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
              <Button
                size="small"
                startIcon={<Add />}
                onClick={() =>
                  patch({
                    saves: [
                      ...node.saves,
                      { action: "db.save", paramsJson: "" },
                    ],
                  } as never)
                }
              >
                save
              </Button>
            </Stack>
          </Box>
        )}
      </Stack>
    </AccordionDetails>
  )

  const renderKindSpecific = () => {
    switch (node.kind) {
      case "action":
        return (
          <Stack spacing={2}>
            <Autocomplete
              options={actionOptions}
              value={actionOptions.find((o) => o.id === node.action) ?? null}
              onChange={(_, v) => patch({ action: v?.id ?? "" } as never)}
              groupBy={(o) => o.group}
              getOptionLabel={(o) => o.label}
              renderInput={(p) => (
                <TextField
                  {...p}
                  label="Действие"
                  required
                  size="small"
                  error={isBlank(node.action)}
                />
              )}
            />
            {node.action && descriptors.find((d) => d.name === node.action) && (
              <ParamsForm
                descriptor={descriptors.find((d) => d.name === node.action)!}
                values={(node as ActionNodeForm).paramsValues}
                onChange={(paramsValues, rawParamsJson) =>
                  patch({ paramsValues, rawParamsJson } as never)
                }
                rawParamsJson={(node as ActionNodeForm).rawParamsJson ?? ""}
                advanced={advanced}
                headHints={allHints}
              />
            )}
            {advanced && !descriptors.some((d) => d.name === node.action) && (
              <TextField
                label="params (raw JSON, для не-описанных действий)"
                value={(node as ActionNodeForm).rawParamsJson ?? ""}
                onChange={(e) =>
                  patch({ rawParamsJson: e.target.value } as never)
                }
                multiline
                minRows={2}
                fullWidth
                size="small"
                InputProps={{ style: { fontFamily: "monospace" } }}
              />
            )}
          </Stack>
        )

      case "branch":
        return (
          <Stack spacing={2}>
            {(node as Extract<NodeFormState, { kind: "branch" }>).cases.map(
              (c, ci) => (
                <Box
                  key={c.key}
                  pl={2}
                  sx={{ borderLeft: 2, borderColor: "warning.main" }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2">Case {ci + 1}</Typography>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        const cases =
                          node.kind === "branch" ? [...node.cases] : []
                        cases.splice(ci, 1)
                        patch({ cases } as never)
                      }}
                    >
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Box my={1}>
                    <ConditionExprEditor
                      value={c.when}
                      onChange={(v) => {
                        const cases =
                          node.kind === "branch" ? [...node.cases] : []
                        cases[ci] = { ...c, when: v }
                        patch({ cases } as never)
                      }}
                      headHints={allHints}
                      limits={limits}
                    />
                  </Box>
                  <Slot
                    nodes={c.then}
                    descriptors={descriptors}
                    nodeKinds={nodeKinds}
                    limits={limits}
                    advanced={advanced}
                    extraHints={extraHints}
                    getResultHints={getResultHints}
                    pathPrefix={`${node.id}/`}
                    onChange={(then: NodeFormState[]) => {
                      const cases =
                        node.kind === "branch" ? [...node.cases] : []
                      cases[ci] = { ...c, then }
                      patch({ cases } as never)
                    }}
                  />
                </Box>
              ),
            )}
            <Button
              size="small"
              startIcon={<Add />}
              onClick={() => {
                const cases = node.kind === "branch" ? [...node.cases] : []
                cases.push({
                  key: uuidv4(),
                  when: { op: "eq", left: true, right: true },
                  then: [],
                })
                patch({ cases } as never)
              }}
            >
              Case
            </Button>
            <Divider>else</Divider>
            <Slot
              nodes={node.elseSteps ?? []}
              descriptors={descriptors}
              nodeKinds={nodeKinds}
              limits={limits}
              advanced={advanced}
              extraHints={extraHints}
              getResultHints={getResultHints}
              pathPrefix={`${node.id}/`}
              onChange={(elseSteps: NodeFormState[]) =>
                patch({ elseSteps } as never)
              }
            />
          </Stack>
        )

      case "forEach":
        return (
          <Stack spacing={2}>
            <TextField
              label="items (JSON-массив или {{...}})"
              value={
                typeof node.kind === "string" && node.kind === "forEach"
                  ? Array.isArray(node.items)
                    ? JSON.stringify(node.items)
                    : String(node.items ?? "")
                  : ""
              }
              onChange={(e) => {
                const raw = e.target.value
                if (/^\{\{[\s\S]+\}\}$/.test(raw)) {
                  patch({ items: raw } as never)
                } else {
                  try {
                    patch({ items: JSON.parse(raw) } as never)
                  } catch {
                    patch({ items: raw } as never)
                  }
                }
              }}
              multiline
              minRows={2}
              fullWidth
              size="small"
              InputProps={{ style: { fontFamily: "monospace" } }}
            />
            <Stack direction="row" spacing={2}>
              <TextField
                label="itemVar"
                value={node.kind === "forEach" ? (node.itemVar ?? "") : ""}
                onChange={(e) => patch({ itemVar: e.target.value } as never)}
                size="small"
                sx={{ flex: 1 }}
              />
              <TextField
                label="maxIterations"
                value={
                  node.kind === "forEach" ? (node.maxIterations ?? "") : ""
                }
                onChange={(e) =>
                  patch({ maxIterations: e.target.value } as never)
                }
                size="small"
                sx={{ flex: 1 }}
              />
            </Stack>
            <Divider>body</Divider>
            <Slot
              nodes={node.kind === "forEach" ? node.body : []}
              descriptors={descriptors}
              nodeKinds={nodeKinds}
              limits={limits}
              advanced={advanced}
              extraHints={extraHints}
              getResultHints={getResultHints}
              pathPrefix={`${node.id}#0/`}
              onChange={(body: NodeFormState[]) => patch({ body } as never)}
            />
          </Stack>
        )

      case "callChain":
        return (
          <Stack spacing={2}>
            <RadioGroup
              row
              value={
                node.kind === "callChain" && node.templateId
                  ? "template"
                  : "inline"
              }
              onChange={(_, v) => {
                if (v === "template") {
                  patch({ templateId: "", inlineSteps: undefined } as never)
                } else {
                  patch({ templateId: undefined, inlineSteps: [] } as never)
                }
              }}
            >
              <FormControlLabel
                value="template"
                control={<Radio size="small" />}
                label="По шаблону"
              />
              <FormControlLabel
                value="inline"
                control={<Radio size="small" />}
                label="Inline"
              />
            </RadioGroup>
            {node.kind === "callChain" && node.templateId !== undefined ? (
              <TextField
                label="templateId"
                value={node.templateId}
                onChange={(e) => patch({ templateId: e.target.value } as never)}
                size="small"
                fullWidth
              />
            ) : (
              <>
                <Divider>inline.steps</Divider>
                <Slot
                  nodes={
                    node.kind === "callChain" ? (node.inlineSteps ?? []) : []
                  }
                  descriptors={descriptors}
                  nodeKinds={nodeKinds}
                  limits={limits}
                  advanced={advanced}
                  extraHints={extraHints}
                  getResultHints={getResultHints}
                  pathPrefix={`${node.id}/`}
                  onChange={(inlineSteps: NodeFormState[]) =>
                    patch({ inlineSteps } as never)
                  }
                />
              </>
            )}
            <TextField
              label="input (JSON)"
              value={node.kind === "callChain" ? (node.inputJson ?? "") : ""}
              onChange={(e) => patch({ inputJson: e.target.value } as never)}
              multiline
              minRows={2}
              fullWidth
              size="small"
              InputProps={{ style: { fontFamily: "monospace" } }}
            />
            <Stack direction="row" spacing={2}>
              <Autocomplete<string, false, false, true>
                freeSolo
                sx={{ flex: 1 }}
                options={clientOptions.map((o) => o.value)}
                // Управляем только inputValue (прецедент TemplatableInput,
                // fieldSpecInput.tsx:103-124): controlled value ломает фильтрацию.
                inputValue={
                  node.kind === "callChain" ? (node.clientId ?? "") : ""
                }
                onInputChange={(_, v, reason) => {
                  if (reason === "input" || reason === "clear")
                    patch({ clientId: v } as never)
                  if (reason === "input") clientsList.setSearch(v)
                }}
                onChange={(_, v) => patch({ clientId: v ?? "" } as never)}
                getOptionLabel={(v) => clientValueLabel(v, clientOptions)}
                filterOptions={(o) => o}
                renderInput={(p) => (
                  <TextField
                    {...p}
                    label="clientId"
                    size="small"
                    helperText="Пусто = in-place подпрограмма; иначе uuid клиента или whole-string {{...}} — спавн цепочки на этом клиенте"
                  />
                )}
                {...infiniteListboxProps({
                  hasMore: clientsList.hasMore,
                  isFetching: clientsList.isFetching,
                  onLoadMore: clientsList.loadMore,
                })}
                renderOption={(props, option, state) =>
                  [
                    props,
                    clientValueLabel(option, clientOptions),
                    state,
                  ] as React.ReactNode
                }
              />
              <TextField
                label="name (имя отчёта спавна)"
                value={node.kind === "callChain" ? (node.name ?? "") : ""}
                onChange={(e) => patch({ name: e.target.value } as never)}
                size="small"
                sx={{ flex: 1 }}
                helperText="Только для спавна (задан clientId); без него — авто callChain:<nodeId>@<parentChainId8>"
              />
            </Stack>
          </Stack>
        )

      case "wait":
        return (
          <Stack spacing={2}>
            <RadioGroup
              row
              value={
                node.kind === "wait" && node.durationMs ? "duration" : "until"
              }
              onChange={(_, v) => {
                if (v === "duration") {
                  patch({ durationMs: "", until: undefined } as never)
                } else {
                  patch({ durationMs: undefined, until: "" } as never)
                }
              }}
            >
              <FormControlLabel
                value="duration"
                control={<Radio size="small" />}
                label="durationMs"
              />
              <FormControlLabel
                value="until"
                control={<Radio size="small" />}
                label="until"
              />
            </RadioGroup>
            {node.kind === "wait" && node.durationMs !== undefined ? (
              <TextField
                label="durationMs"
                value={node.durationMs}
                onChange={(e) => patch({ durationMs: e.target.value } as never)}
                size="small"
                fullWidth
                helperText="Число мс или whole-string {{random.result.value}}"
              />
            ) : (
              <TextField
                label="until (ISO или {{...}})"
                value={node.kind === "wait" ? (node.until ?? "") : ""}
                onChange={(e) => patch({ until: e.target.value } as never)}
                size="small"
                fullWidth
              />
            )}
          </Stack>
        )

      case "flow.onUpdate": {
        const fo = node as Extract<NodeFormState, { kind: "flow.onUpdate" }>
        return (
          <Stack spacing={2}>
            <ConditionExprEditor
              value={fo.match}
              onChange={(v) => patch({ match: v } as never)}
              headHints={allHints}
              limits={limits}
            />
            <TextField
              label="maxFires"
              value={fo.maxFires ?? ""}
              onChange={(e) => patch({ maxFires: e.target.value } as never)}
              size="small"
              fullWidth
              helperText="Пусто = без лимита; целое > 0 или whole-string {{...}}-шаблон"
            />
            <Divider>steps</Divider>
            <Slot
              nodes={fo.steps ?? []}
              descriptors={descriptors}
              nodeKinds={nodeKinds}
              limits={limits}
              advanced={advanced}
              extraHints={extraHints}
              getResultHints={getResultHints}
              pathPrefix={`${node.id}/`}
              onChange={(steps: NodeFormState[]) => patch({ steps } as never)}
            />
          </Stack>
        )
      }
    }
  }

  return (
    <Box>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ width: "100%", pr: 2 }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                px: 1,
                borderRadius: 1,
                bgcolor: `${KIND_COLORS[node.kind]}.main`,
                color: `${KIND_COLORS[node.kind]}.contrastText`,
              }}
            >
              {node.kind}
            </Typography>
            <Typography variant="body2" sx={{ flex: 1 }}>
              {node.id || "(без id)"}
            </Typography>
            <Box>
              <IconButton
                size="small"
                disabled={index === 0}
                onClick={(e) => {
                  e.stopPropagation()
                  onMove(-1)
                }}
              >
                <ArrowUpward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                disabled={index === total - 1}
                onClick={(e) => {
                  e.stopPropagation()
                  onMove(1)
                }}
              >
                <ArrowDownward fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                color="error"
                onClick={(e) => {
                  e.stopPropagation()
                  onRemove()
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            </Box>
          </Stack>
        </AccordionSummary>
        {renderCommonFields()}
        <AccordionDetails>
          <Divider sx={{ mb: 2 }} />
          {renderKindSpecific()}
        </AccordionDetails>
      </Accordion>
      {issues && issues.length > 0 && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {issues.map((iss, i) => (
            <div key={i}>
              {iss.path ? `${iss.path}: ` : ""}
              {iss.message}
            </div>
          ))}
        </Alert>
      )}
    </Box>
  )
}
